/**
 * @fileoverview Core business types for Agent Console features.
 * Types and constants are derived from shared_constants.json (the source of truth).
 * @owner @Appboy/agent-console
 */

import type { I18nT } from "@lib/dashboardI18n";
import sharedConstants from "@lib/shared_constants";

const AI_SETTINGS = sharedConstants.AI_SETTINGS;

/**
 * Interaction site where an AI agent can be used.
 * - 0 = Canvas: Agent is used in Canvas workflows
 * - 1 = Catalog: Agent is used in Catalog contexts
 */
export type InteractionSite = (typeof INTERACTION_SITES)[keyof typeof INTERACTION_SITES];

/** Constants for interaction sites. Derived from shared_constants.json. */
export const INTERACTION_SITES = AI_SETTINGS.INTERACTION_SITES;

/**
 * QA agent interaction sites (Campaign QA and Canvas QA). QA agents are
 * configured with rulesets instead of a prompt and skip the Output wizard step.
 */
const qaInteractionSites = new Set<InteractionSite>([
  INTERACTION_SITES.CAMPAIGN_SETUP,
  INTERACTION_SITES.CANVAS_QA,
]);

/** Returns true when the interaction site is a QA agent type (Campaign QA or Canvas QA). */
export const isQaInteractionSite = (site: InteractionSite | undefined): boolean => {
  return site !== undefined && qaInteractionSites.has(site);
};

const interactionSiteValues = new Set<number>(Object.values(INTERACTION_SITES));

/** Type guard validating an untrusted value (e.g. a URL param) as a known interaction site. */
export const isInteractionSite = (value: number): value is InteractionSite => {
  return interactionSiteValues.has(value);
};

/**
 * Thinking/reasoning level for AI model responses.
 * - "none": No extended thinking (lowest cost)
 * - "low": Low thinking effort
 * - "medium": Medium thinking effort
 * - "high": High thinking effort (highest cost, most thorough)
 */
export type ThinkingLevel = (typeof THINKING_LEVELS)[keyof typeof THINKING_LEVELS];

/** Constants for thinking levels. Derived from shared_constants.json. */
export const THINKING_LEVELS = AI_SETTINGS.THINKING_LEVELS;

/**
 * Braze-added context type appended to an agent prompt at execution time. The content is
 * never shown to customers; invocation logs surface only these type names.
 *
 * Sourced from `Braze::Agentic::BrazeContextTypes::Keys` via shared_constants.json, so adding
 * a type in Ruby makes any exhaustive mapping over this union fail to compile until it is
 * handled on the frontend.
 */
export type BrazeContextType = (typeof BRAZE_CONTEXT_TYPES)[keyof typeof BRAZE_CONTEXT_TYPES];

/** Constants for Braze-added context types. Derived from shared_constants.json. */
export const BRAZE_CONTEXT_TYPES = AI_SETTINGS.BRAZE_CONTEXT_TYPES;

const BRAZE_CONTEXT_TYPE_VALUES = new Set<string>(Object.values(BRAZE_CONTEXT_TYPES));

/**
 * Type guard: returns true when `type` is a Braze-added context type known to this build.
 * Invocation logs are stored in Mongo, so a log written by a newer backend can carry a key
 * this build cannot label — narrow with this rather than assuming the union is exhaustive.
 */
export const isBrazeContextType = (type: string | undefined): type is BrazeContextType => {
  return type !== undefined && BRAZE_CONTEXT_TYPE_VALUES.has(type);
};

/**
 * Preset ruleset type identifier.
 * Preset rulesets have an immutable title derived from the type, and may carry
 * type-specific behaviors (e.g. attaching liquid instructions for
 * "personalization_and_dynamic_content").
 *
 * Sourced from `Braze::Agentic::Ruleset::PresetTypes` via shared_constants.json.
 */
export type PresetRulesetType = (typeof PRESET_RULESET_TYPES)[keyof typeof PRESET_RULESET_TYPES];

/** Constants for preset ruleset types. Derived from shared_constants.json. */
export const PRESET_RULESET_TYPES = AI_SETTINGS.QA_AGENTS.PRESET_RULESET_TYPES;

const PRESET_RULESET_TYPE_VALUES = new Set<string>(Object.values(PRESET_RULESET_TYPES));

/** Type guard: returns true when `type` is a known preset ruleset type. */
export const isPresetRulesetType = (type: string | undefined): type is PresetRulesetType => {
  return type !== undefined && PRESET_RULESET_TYPE_VALUES.has(type);
};

/**
 * Ruleset scope identifier. Distinguishes which Canvas QA tab a ruleset belongs
 * to: `canvas` rulesets are evaluated once for the Canvas (Canvas Configuration
 * tab), while the step scopes are evaluated per canvas step of their type under
 * the Canvas steps tab (`message_step` per message step, `audience_paths_step`
 * per Audience Paths step). Campaign QA rulesets leave scope undefined.
 *
 * Sourced from `Braze::Agentic::Ruleset::Scopes` via shared_constants.json.
 */
export type RulesetScope = (typeof RULESET_SCOPES)[keyof typeof RULESET_SCOPES];

/** Constants for ruleset scopes. Derived from shared_constants.json. */
export const RULESET_SCOPES = AI_SETTINGS.QA_AGENTS.RULESET_SCOPES;

/**
 * The ruleset scopes evaluated per canvas step (as opposed to once per
 * Canvas): `message_step` per message step, `audience_paths_step` per
 * Audience Paths step. All step scopes run together as the single "Canvas
 * steps" run: one request, one run id, one panel section. The frontend mirror
 * of `Braze::Agentic::Ruleset::Scopes.step_scopes`; see `isStepRulesetScope`.
 */
export type StepRulesetScope = typeof RULESET_SCOPES.MESSAGE_STEP | typeof RULESET_SCOPES.AUDIENCE_PATHS_STEP;

/**
 * Constants for the Standards agent types presets are offered for.
 * Derived from shared_constants.json.
 */
export const presetAgentTypes = AI_SETTINGS.QA_AGENTS.PRESET_AGENT_TYPES;

/**
 * A Standards agent type presets are offered for: Campaign QA or Canvas QA.
 *
 * Sourced from `Braze::Agentic::RulesetPresets::AgentTypes` via shared_constants.json.
 */
export type QaPresetAgentType = (typeof presetAgentTypes)[keyof typeof presetAgentTypes];

/**
 * Constants for the extra context a ruleset can receive when it is evaluated.
 * Derived from shared_constants.json.
 */
export const rulesetContexts = AI_SETTINGS.QA_AGENTS.RULESET_CONTEXTS;

/**
 * Extra context a ruleset receives when it is evaluated: the Braze Liquid
 * reference, accessibility scan results, the attached brand guideline, or the
 * message's images. Which rulesets receive which lives in `rulesetPresets.ts`.
 *
 * Sourced from `Braze::Agentic::RulesetPresets::Contexts` via shared_constants.json.
 */
export type RulesetContext = (typeof rulesetContexts)[keyof typeof rulesetContexts];

/** One agent type's definition of a preset ruleset, from `Braze::Agentic::RulesetPresets::DEFINITIONS`. */
export interface PresetDefinition {
  /** The scope the preset's rulesets carry; null for Campaign QA, whose rulesets are unscoped. */
  scope: RulesetScope | null;
  contexts: readonly RulesetContext[];
}

/** Everything the dashboard says about one evaluation context a ruleset can receive. */
export interface RulesetContextCopy {
  /**
   * The note under a ruleset in the rules UI for each context it receives.
   * Null where the rules UI shows the context another way, or makes no promise
   * about it. The table in `rulesetContextCopy.ts` spells its `t()` calls out
   * inline so `yarn translations:parse` can still harvest the keys.
   */
  ruleListNote: ((t: I18nT<"custom-agent-form">) => string) | null;
  /**
   * How Operator describes the context, given the noun of the summary it
   * arrives with.
   */
  operatorDescription: (summaryNoun: string) => string;
  /**
   * How Operator is told that a preset does *not* receive this context although
   * the same preset receives it on the other Standard kind. Without it, a bare
   * summary description reads as a complete account, and Operator can file (say)
   * WCAG rules under a preset whose `useFor` mentions accessibility believing a
   * scan backs them. Null where the absence needs no mention.
   */
  absenceNote: string | null;
  /**
   * The rule theme this context serves, for Operator's "which category"
   * guidance. Null for context every ruleset in a scope receives, where the
   * category choice makes no difference.
   */
  ruleTheme: string | null;
}

/**
 * Maps one of the Braze provider's policy model ids ("auto", "auto_pro") to its display label, or
 * returns undefined for any other model id so the caller falls back to its own name. Get one from
 * `useBrazeModelLabel`, which holds the translated labels.
 */
export type BrazeModelLabel = (model: string | null | undefined) => string | undefined;

/** Options for `useBrazeModelLabel`. */
export interface UseBrazeModelLabelOptions {
  /**
   * The model ids come from a past record (an invocation log or a saved agent version), which keeps
   * the label of the model it recorded. Otherwise the ids name the model an agent runs on now, and
   * "auto_pro" is labeled Braze Auto while the agentic_braze_auto_pro flipper is off, because the
   * agent then runs as Auto.
   */
  isPastRecord?: boolean;
}
